import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import {
  moveInFlatLists,
  rollbackMove,
  type MoveSnapshot,
} from '../../boards/optimisticMove';
import type {
  ProductItem,
  ProductItemCustomer,
  ProductItemPriority,
  ProductItemStage,
} from '../types';

interface ProductItemFilters {
  search?: string;
  /** Priority levels to include (p0–p3). */
  priorities?: ProductItemPriority[];
  /** Filter to items linked to this Account board card. */
  accountCardId?: string;
}

const queryKey = (includeDeprecated: boolean, filters?: ProductItemFilters) =>
  [
    'product-items',
    {
      includeDeprecated,
      search: filters?.search?.trim(),
      priorities: filters?.priorities ?? [],
      accountCardId: filters?.accountCardId,
    },
  ] as const;

async function getJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include', ...init });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Request failed (${res.status}): ${body}`);
  }
  return (await res.json()) as T;
}

export function useProductItems(includeDeprecated: boolean, options?: ProductItemFilters) {
  const search = options?.search?.trim();
  const priorities = options?.priorities ?? [];
  const accountCardId = options?.accountCardId;

  return useQuery({
    queryKey: queryKey(includeDeprecated, { search, priorities, accountCardId }),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (includeDeprecated) params.set('includeDeprecated', 'true');
      if (search) params.set('search', search);
      if (priorities.length > 0) params.set('priority', priorities.join(','));
      if (accountCardId) params.set('accountCardId', accountCardId);
      const qs = params.toString();
      const res = await getJson<{ ok: true; data: ProductItem[] }>(
        `/org/product-items${qs ? `?${qs}` : ''}`
      );
      return res.data;
    },
  });
}

export function useCreateProductItem(_includeDeprecated: boolean) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      title: string;
      description?: string;
      stage?: ProductItemStage;
      priority?: ProductItemPriority | null;
    }) => {
      const res = await getJson<{ ok: true; data: ProductItem }>(`/org/product-items`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['product-items'] });
    },
  });
}

export function useUpdateProductItem(_includeDeprecated: boolean) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      title?: string;
      description?: string;
      priority?: ProductItemPriority | null;
    }) => {
      const { id, ...body } = input;
      const res = await getJson<{ ok: true; data: ProductItem }>(`/org/product-items/${id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['product-items'] });
    },
  });
}

export function useMoveProductItem(_includeDeprecated: boolean) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      stage: ProductItemStage;
      beforeItemId: string | null;
      afterItemId: string | null;
    }) => {
      const { id, ...body } = input;
      const res = await getJson<{ ok: true; data: ProductItem }>(`/org/product-items/${id}/move`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      return res.data;
    },
    onMutate: async (input): Promise<{ snapshot: MoveSnapshot }> => {
      // Prefix-match so EVERY cached filter variant (search/priority/customer/
      // includeDeprecated) gets the optimistic splice — an exact key only hits
      // the filterless variant, leaving filtered boards to snap back.
      await qc.cancelQueries({ queryKey: ['product-items'] });
      const snapshot = moveInFlatLists<ProductItem>(qc, ['product-items'], {
        itemId: input.id,
        updateItem: (it) => ({ ...it, stage: input.stage }),
        target: { beforeItemId: input.beforeItemId, afterItemId: input.afterItemId },
        getId: (it) => it.id,
        isSameColumn: (it) => it.stage === input.stage,
      });
      return { snapshot };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.snapshot) rollbackMove(qc, ctx.snapshot);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['product-items'] });
    },
  });
}

export function useDeleteProductItem(_includeDeprecated: boolean) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await getJson<{ ok: true }>(`/org/product-items/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['product-items'] });
    },
  });
}

export function useAddProductItemCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      productItemId: string;
      accountCardId?: string;
      orgId?: string;
      name?: string;
    }) => {
      const { productItemId, ...body } = input;
      const res = await getJson<{ ok: true; data: ProductItemCustomer }>(
        `/org/product-items/${productItemId}/customers`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['product-items'] });
    },
  });
}

export function useRemoveProductItemCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { productItemId: string; customerId: string }) => {
      await getJson<{ ok: true }>(
        `/org/product-items/${input.productItemId}/customers/${input.customerId}`,
        { method: 'DELETE' }
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['product-items'] });
    },
  });
}
